import type { MosaicNode } from 'react-mosaic-component'
import type { OverlayStyleOverride } from '@edge/charts'
import type { ComponentType, TabGroup, LayoutMode, GridCell } from '@/types/schemas/workspace'
import type { Feature, PlanTier } from '@/context/plan-constraint-utils'
import type { TreeItems } from './tree-state-manager'

// ─── Link Mode ───

/** Controls how ticker/symbol changes propagate between panels. */
export type LinkMode = 'onChange' | 'purple' | 'blue' | 'green' | 'yellow' | 'none'

export const COLOR_LINK_MODES = ['purple', 'blue', 'green', 'yellow'] as const

export function isColorLinkMode(mode: LinkMode): mode is 'purple' | 'blue' | 'green' | 'yellow' {
  return (COLOR_LINK_MODES as readonly string[]).includes(mode)
}

// ─── Panel State ───

/**
 * Generic panel state — stored per panel in refs.
 * Feature-specific panels extend this with their own fields
 * by casting via useLayoutPanel<T>().
 */
export type PanelState = Record<string, unknown>

// ─── Window Config ───

/** Per-window config stored alongside the mosaic tree. */
export interface WindowConfig {
  symbolIndex: number
  componentType: ComponentType
  /**
   * When false, the panel top bar collapses to a 6px drag-grip strip.
   * Hovering the strip reveals the full header as an absolute overlay.
   * Defaults to true (header visible) when absent.
   */
  headerVisible?: boolean
  linkMode?: LinkMode
  tickerCleared?: boolean
}

// ─── Panel Component Props ───

/** Standard props received by every panel component via the registry. */
export interface PanelProps {
  panelId: string
  windowConfig: WindowConfig
  symbols: string[]
  /**
   * Select a ticker from inside the panel. `opts.skipSource` broadcasts the
   * ticker to linked peers WITHOUT updating this panel's own symbol/config
   * (the legacy app's IGNORE_SELF dispatch): used by feed-style panels (news
   * row click) whose own view must not re-filter on every click.
   */
  onSelectTicker?: (
    ticker: string,
    focusDate?: string | null,
    opts?: { skipSource?: boolean },
  ) => void
  /**
   * Clear the selection this panel broadcast: it and the panels its broadcast
   * reaches show no ticker and no day (their empty state) until the next
   * selection. Records calls it when its result set lists no records. A locked
   * panel is not reached, so it keeps its own.
   */
  onClearSelection?: () => void
  /** Date string (YYYY-MM-DD) to rewind the linked chart to, or null for real-time */
  focusDate?: string | null
  /**
   * Component type that produced the current `focusDate`. Panels gate on it via
   * `followsFocusBroadcast` so a source can drive only the panels it should
   * (e.g. a Ticker Stats Records row click rewinds charts, not scanners).
   * Absent means "unknown source" — treated as permitted.
   */
  focusDateSource?: ComponentType | null
  /** Set the focus date (historical rewind) or null to return to real-time */
  onFocusDate?: (date: string | null) => void
  /** Register a handler for toolbar action clicks. Called by panels on mount. */
  onRegisterToolbarHandler?: (
    handler: (actionId: string, anchor?: { x: number; y: number }) => void,
  ) => void
  /** Register which toolbar action IDs to display. Overrides the static per-component-type list. */
  onRegisterPanelActions?: (actionIds: string[]) => void
  /** Register badge counts for toolbar action buttons (e.g. { filters: 7 }). */
  onRegisterActionBadges?: (badges: Record<string, number>) => void
}

/** A React component that can render inside a workspace panel. */
export type PanelComponent = React.ComponentType<PanelProps>

// ─── Tree Mutation Types ───

/** A single operation in a tree mutate batch. */
export type MutateTreeOp =
  | {
      op: 'create'
      id: string
      parentId: string
      index?: number
      node: { name: string; isFolder: boolean }
      layoutData?: Record<string, unknown>
    }
  | { op: 'delete'; nodeIds: string[] }
  | { op: 'move'; nodeIds: string[]; parentId: string; index: number }
  | { op: 'rename'; nodeId: string; name: string }
  | { op: 'duplicate'; nodeId: string; idMap: Record<string, string>; name: string }
  | { op: 'restore'; nodeId: string }
  | { op: 'set_active'; layoutId: string | null }
  | { op: 'set_open_state'; openState: string[] }

/** Result from POST /mutate. */
export interface MutateTreeResult {
  workspaceRevision: number
  createdIds: Record<string, string>
}

// ─── Shared Data Types ───

export interface TemplateData {
  templateId: string
  name?: string
  chartType?: string
  mainIndicators?: unknown[]
  subIndicators?: unknown[]
  settings?: Record<string, unknown>
  createdAt?: string
  updatedAt?: string
}

export interface SavedDrawing {
  syncId: string
  name: string
  points: Array<{ timestamp: number; value: number }>
  /** Per-drawing style override (color / width / line-style). Absent on legacy
   *  drawings — they load with the overlay's default styling. */
  styles?: OverlayStyleOverride
  /** When false, the drawing is hidden on the chart but kept in state. */
  visible?: boolean
  /** When true, the drawing is locked (not draggable). */
  lock?: boolean
  /** Per-instance non-geometric state (text content, labels). Opaque —
   *  only the owning overlay template interprets it. */
  extendData?: unknown
}

export interface LayoutData {
  mosaicTree: unknown
  windowConfigs: Record<string, WindowConfig>
  globalSettings?: Record<string, unknown>
  /** Optional pane tab-groups, keyed by host leaf panel ID. Absent = single-component panes. */
  tabGroups?: Record<string, TabGroup>
  /** Renderer for this layout. Absent = 'mosaic'. */
  layoutMode?: LayoutMode
  /** react-grid-layout arrangement for 'grid' mode. Absent for mosaic layouts. */
  gridLayout?: GridCell[]
  /**
   * Mirror of the tree-item name. Optional for backwards compat; new saves
   * include the current name so the body and tree stay aligned.
   */
  name?: string
}

/** Layout data with embedded panel states — used for single-document save/load. */
export interface FullLayoutData extends LayoutData {
  panels: Record<string, PanelState>
  updatedAt?: string
  layoutRevision?: number
}

/**
 * Thrown when a conditional write is rejected (server returned 409).
 * Re-exported from `services/api/errors.ts` — canonical definition lives
 * there alongside the ApiClient client that throws it. Workspace consumers
 * can keep importing from `@/workspace/types` for back-compat.
 */
export { ConflictError } from '@/services/api/errors'

export interface LayoutSummary {
  layoutId: string
  name: string
  updatedAt?: string
  folderId?: string | null
  sortOrder?: number
}

export interface BootstrapData {
  treeItems: TreeItems
  treeOpenState: string[]
  activeLayoutId: string | null
  activeLayout: LayoutData | null
  panels: PanelState[]
  workspaceRevision?: number
}

// ─── Panel Seed ───

/**
 * Starting states for a panel the user just added with no state of its own,
 * keyed by panel id (the panel itself plus any `<panelId>::<cellIdx>` chart
 * cells). Null leaves the panel bare.
 */
export type PanelSeed = (
  componentType: ComponentType,
  panelId: string,
) => Record<string, Record<string, unknown>> | null

// ─── Preset Layout ───

/** Named icons a preset may show in the layout rail (layout-tree-sidebar.tsx). */
export type PresetIcon = 'tags'

/** Shared preset type used by both dashboard and history workspaces. */
export interface PresetLayout {
  presetId: string
  name: string
  description: string
  category?: string
  /**
   * Icon for the preset's row in the layout rail, by name (JSON presets cannot
   * hold a component). Absent = the generic layout-template icon.
   */
  icon?: PresetIcon
  requiredPlan: PlanTier
  /**
   * The feature to pitch when `requiredPlan` is what refuses the launch. Only
   * needed when no panel in the preset is itself gated (a preset held back by
   * the DATA a panel shows rather than by the panel): without it a plan-floor
   * refusal has nothing to name, and a refusal must always be able to sell the
   * upgrade rather than dead-end.
   */
  upgradeFeature?: Feature
  mosaicTree: MosaicNode<string>
  windowConfigs: Record<string, WindowConfig>
  /** Optional pane tab-groups, keyed by host leaf panel ID. Tab members are extra (non-leaf) panel IDs. */
  tabGroups?: Record<string, TabGroup>
  /**
   * Renderer this preset seeds. Absent = 'mosaic'. When 'grid' and no
   * `gridLayout` is provided, the layout store derives the grid from
   * `mosaicTree` via mosaicToGrid at seed time.
   */
  layoutMode?: LayoutMode
  /** Optional explicit react-grid-layout arrangement for 'grid' presets. */
  gridLayout?: GridCell[]
  /**
   * 'grid' presets only: every cell takes the height its content needs, and
   * nothing drags or resizes. The page is a read-through scroll of tiles
   * sized to their data (the report's Distributions page, Chris 2026-09-06).
   * The `gridLayout` heights are then only the first paint before measuring.
   */
  gridFitContent?: boolean
  /**
   * 'grid' presets only: cells keep the heights in `gridLayout` and never drag
   * or resize. Unlike gridFitContent, nothing is measured, so panels that fill
   * their parent (charts) keep their set height.
   */
  gridFixed?: boolean
  /**
   * Optional default per-panel states, keyed by panel ID. Threaded into
   * WorkspaceMosaic's `defaultPanels` prop → layout-store `defaults.panels`.
   * Persisted user panel states merge on top per key.
   */
  panels?: Record<string, Record<string, unknown>>
  /**
   * History presets only: the built-in filter profile the preset opens on the
   * first time it is visited (DefaultFiltersBridge). Absent = no seed, the
   * page keeps whatever filters it has.
   */
  filterProfileId?: string
}
